import { useCallback } from 'react';
import { useSetAtom } from 'jotai';
import { confirmModalAtom } from '@/atom/modal';
import { DestinationCGType } from '@/types/graphql/root';
import { removeSlot } from '@/utils/actions';
import { getSortedDestinationList, filterNullUndefined } from '@/utils';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { historyStackAtom } from '@/atom/historyStack';
import { history } from '@/utils/history';
import { TRIP_MODE, TripModeType } from '@/app/ui/trip/client-trip-page';

interface UseTripItineraryInteractionsProps {
  tripId: string;
  mode: TripModeType;
  destinations: DestinationCGType[];
  setDestinations: React.Dispatch<React.SetStateAction<DestinationCGType[]>>;
  setSelectedDestIdList: React.Dispatch<React.SetStateAction<string[]>>;
  setMemoDictionary: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  scrollListToDestOnTop: (destId: string, dayNumber: number, isSmooth?: boolean, initStickyHeight?: number) => void;
}

export default function useTripItineraryInteractions({
  tripId,
  mode,
  destinations,
  setDestinations,
  setSelectedDestIdList,
  setMemoDictionary,
  scrollListToDestOnTop,
}: UseTripItineraryInteractionsProps) {
  const setConfirmModal = useSetAtom(confirmModalAtom);
  const setHistoryStack = useSetAtom(historyStackAtom);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const getAccommRelationDests = useCallback(
    (destId: string): DestinationCGType[] => {
      const dest = destinations.find((_d) => _d.id === destId);
      if (!dest?.accommType) return [];

      if (dest.accommType === 'night') {
        const nextMorningAccomm = destinations.find((_d) => {
          return _d.accommType === 'morning' && dest.day && _d.day === dest.day + 1;
        });
        if (nextMorningAccomm) return [dest, nextMorningAccomm];
      } else if (dest.accommType === 'morning') {
        const prevNightAccomm = destinations.find((_d) => {
          return _d.accommType === 'night' && dest.day && _d.day === dest.day - 1;
        });
        if (prevNightAccomm) return [prevNightAccomm, dest];
      }

      return [];
    },
    [destinations],
  );

  const onClickPoiDetail = useCallback(
    (dest: DestinationCGType) => {
      if (mode !== TRIP_MODE.VIEW) return;

      const alreadyPoiId = searchParams.get('poiId');

      if (alreadyPoiId) {
        history?.replace(`/trip/${tripId}?poiId=${dest.poi?.id}`);
      } else {
        history?.push(`/trip/${tripId}?poiId=${dest.poi?.id}`);
      }
      if (dest.id && dest.day) scrollListToDestOnTop(dest.id, dest.day);
    },
    [mode, tripId, pathname, history, scrollListToDestOnTop, searchParams],
  );

  const onClickRemoveSlot = useCallback(
    (destId: string) => {
      const dest = destinations.find((_d) => _d.id === destId);
      if (!dest) return;

      history?.push(pathname);
      setHistoryStack((prev) => [
        ...prev,
        {
          name: '슬롯 삭제 ConfirmModal 종료 (from useTripItineraryInteractions)',
          afterAction: () => {
            setConfirmModal({ visible: false });
          },
        },
      ]);
      setConfirmModal({
        visible: true,
        title: '이 여행지를 삭제하시겠어요?',
        message: '일정표에서 영구적으로 삭제되며,\n복구 및 취소는 불가능해요.',
        onClose: () => router.back(),
        onConfirm: async () => {
          let idsToRemove: { id?: string | null; day?: number | null; placeId?: string | null }[] = [];

          if (dest.accommType) {
            const relatedAccommDests = getAccommRelationDests(destId);
            idsToRemove = relatedAccommDests.map((_d) => ({
              id: _d.id,
              day: _d.day,
              placeId: _d.poi?.placeId,
            }));
          } else {
            idsToRemove = [
              {
                id: destId,
                day: dest.day,
                placeId: dest.poi?.placeId,
              },
            ];
          }

          removeSlot(tripId, idsToRemove).then((dests) => {
            const successfullyRemovedIds = filterNullUndefined(dests).map((_d) => _d?.id);
            setDestinations((prev) =>
              getSortedDestinationList(prev.filter((_d) => !successfullyRemovedIds.includes(_d.id))),
            );
            setSelectedDestIdList((prev) => prev.filter((id) => !successfullyRemovedIds.includes(id)));
          });

          router.back();
        },
      });
    },
    [tripId, pathname, getAccommRelationDests],
  );

  const onToggleSelectDest = useCallback(
    (destId: string) => {
      setSelectedDestIdList((prev) => {
        if (prev.includes(destId)) {
          return prev.filter((id) => id !== destId);
        } else {
          return [...prev, destId];
        }
      });
    },
    [setSelectedDestIdList],
  );

  const onUpdateMemo = useCallback(
    (destId: string, memo: string) => {
      setMemoDictionary((prev) => ({
        ...prev,
        [destId]: memo,
      }));
    },
    [setMemoDictionary],
  );

  return {
    onClickPoiDetail,
    onClickRemoveSlot,
    onToggleSelectDest,
    onUpdateMemo,
  };
}
